import React from 'react';
import { Banknote, Building2, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { ResponsiveAmount, AccountCodeLink, formatResponsiveAmount } from '../../ui/ResponsiveAmount';

interface CashWidgetProps {
  data: any;
}

/**
 * Comptes SYSCOHADA de trésorerie
 */
const TREASURY_ACCOUNTS = {
  caisse: { code: '571', label: 'Caisse' },
  banque: { code: '512', label: 'Banques' },
  ccp: { code: '514', label: 'CCP' },
  tresorerie: { code: '5', label: 'Classe 5 - Trésorerie' }
};

export function CashWidget({ data }: CashWidgetProps) {
  const cashPosition = data?.cashPosition;
  
  if (!cashPosition) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p className="text-sm">Aucune donnée disponible</p>
      </div>
    );
  }

  const totalCash = cashPosition.totalCash || 0;
  const cashOnHand = cashPosition.cashOnHand || 0;
  const bankBalance = cashPosition.bankBalance || 0;
  const cashFlow = cashPosition.cashFlow || {};
  
  const inflows = cashFlow.inflows || 0;
  const outflows = cashFlow.outflows || 0;
  const netFlow = inflows - outflows;

  const cashBreakdown = [
    { 
      icon: Wallet, 
      label: 'Caisse', 
      value: cashOnHand, 
      color: 'text-emerald-600 dark:text-emerald-400',
      bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
      barColor: 'bg-emerald-500',
      accountCode: TREASURY_ACCOUNTS.caisse.code
    },
    { 
      icon: Building2, 
      label: 'Banque', 
      value: bankBalance, 
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      barColor: 'bg-blue-500',
      accountCode: TREASURY_ACCOUNTS.banque.code
    }
  ];

  return (
    <div className="space-y-4">
      {/* Total Cash Header */}
      <div className="flex items-center justify-between pb-3 border-b border-border">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Trésorerie Totale</p>
            <AccountCodeLink 
              code={TREASURY_ACCOUNTS.tresorerie.code} 
              variant="subtle" 
              showIcon 
            />
          </div>
          <ResponsiveAmount 
            value={totalCash}
            size="2xl"
            className="font-bold"
            colorize
          />
        </div>
        <div className={`p-2 rounded-lg flex-shrink-0 ${totalCash >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
          <Banknote className={`h-5 w-5 ${totalCash >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} />
        </div>
      </div>

      {/* Cash Breakdown */}
      <div className="space-y-3">
        {cashBreakdown.map((item) => {
          const Icon = item.icon;
          const percentage = totalCash > 0 ? (item.value / totalCash) * 100 : 0;
          
          return (
            <div key={item.label} className="space-y-1">
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded-md flex-shrink-0 ${item.bgColor}`}>
                  <Icon className={`h-4 w-4 ${item.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                      <AccountCodeLink 
                        code={item.accountCode} 
                        variant="subtle"
                      />
                    </div>
                    <ResponsiveAmount 
                      value={item.value}
                      size="sm"
                      className="flex-shrink-0"
                    />
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="ml-10">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${item.barColor}`}
                    style={{ width: `${Math.min(100, percentage)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Cash Flow Summary */}
      {(inflows > 0 || outflows > 0) && (
        <div className="pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Flux de trésorerie (période)</p>
          <div className="flex items-center justify-between text-xs gap-2 flex-wrap">
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <ArrowDownRight className="h-3 w-3 flex-shrink-0" />
              <span className="hidden sm:inline">+</span>
              <span className="tabular-nums">{formatResponsiveAmount(inflows, true)}</span>
            </div>
            <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
              <ArrowUpRight className="h-3 w-3 flex-shrink-0" />
              <span className="hidden sm:inline">-</span>
              <span className="tabular-nums">{formatResponsiveAmount(outflows, true)}</span>
            </div>
            <div className={`font-medium tabular-nums ${netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              = {netFlow >= 0 ? '+' : ''}{formatResponsiveAmount(netFlow, true)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
